// Smart Create Full-name autocomplete — TYPE-AWARE suggestion selection mapper
// (marker cp-suggest-orgmap-v1).
//
// BUG this fixes (2026-07-04): the Full-name field's contact-match dropdown is
// fed by /api/crm/search/unified, which matches ORGANIZATION records too (e.g.
// via a contact person's email like joe.nieves@stratacon.com). The old click
// handler pasted `preferred_name` straight into the person Full-name field
// regardless of `client_type`, so picking "Adam and Joe Know Lunch" put an ORG
// NAME into a PERSON name field — which then even tripped the couple-"and"
// person-name validation (cp-name-normalize-v1). Org names belong in the
// Organization Name field, which is charset/couple-exempt by design
// (validateOrgNameInput).
//
// Mapping rules:
//   PERSON record       → identical to the historical behavior: record name /
//                         email / phone / address fill the card; the
//                         Organization Name field is left untouched.
//   ORGANIZATION record → the record display name goes to the Organization
//                         Name field (`company`), NEVER the person Full name.
//                         The person card prefills from the org's PRIMARY
//                         contact person when one is on file (name/email/phone,
//                         falling back per-field to the record-level values);
//                         with NO person on file the card KEEPS whatever the
//                         user had typed and email/phone come from the
//                         record-level values as before. Address/city/state
//                         prefill stays record-level in both cases.
//
// The returned matchSnapshot mirrors the values actually placed in the form so
// a fresh selection is never immediately "dirty" (the Update-Contact diff and
// the rename-breaks-match rules in App.tsx compare against this snapshot).
//
// Pure: no DOM, no React, no I/O — vitest-gated under `npm test`
// (CLAUDE.md critical rule 12).

// Minification-proof shipped-bundle marker. Referenced as a data attribute
// (data-cp-suggest) on the Full-name suggestion list container in src/App.tsx
// so it can never be tree-shaken out.
export const CREATE_SUGGEST_ORGMAP_MARKER = 'cp-suggest-orgmap-v1'

/** A unified-search contact suggestion as consumed by the Smart Create panel.
 *  primary_person_* are the backward-compatible additions returned for
 *  organization rows (the org's primary contact person, when on file). */
export interface SuggestionRecord {
  unified_client_id: string
  preferred_name?: string | null
  preferred_email?: string | null
  preferred_phone?: string | null
  preferred_street?: string | null
  preferred_city?: string | null
  preferred_state?: string | null
  client_type?: string | null
  org_primary_sub_type?: string | null
  primary_person_name?: string | null
  primary_person_email?: string | null
  primary_person_phone?: string | null
}

export interface SuggestionSelection {
  isOrg: boolean
  /** Value for the Organization Name field. null ⇒ leave the field untouched
   *  (person records never claim it). */
  company: string | null
  /** Matched org's primary sub-type ('' for person records) — feeds the
   *  read-only "Organization type" token. */
  orgSubType: string
  fields: {
    /** Person Full-name field. NEVER the org display name for org records. */
    name: string
    email: string
    phone: string
    street: string
    city: string
    state: string
    zip: string
    clientType: 'person' | 'organization'
  }
  /** Snapshot for the Update-Contact diff + rename-breaks-match rules. */
  matchSnapshot: {
    name: string
    company: string
    email: string
    phone: string
    street: string
    city: string
    state: string
    zip: string
  }
  autoFilled: {
    email: boolean
    phone: boolean
    street: boolean
    city: boolean
    state: boolean
  }
}

/** Map a clicked Full-name autocomplete suggestion onto the Smart Create form,
 *  by record type. `typedName` is what the user had typed in the Full-name
 *  field at click time — an org selection keeps it when the org has no primary
 *  contact person on file. */
export function mapSuggestionSelection(
  record: SuggestionRecord,
  typedName: string,
): SuggestionSelection {
  const isOrg = (record.client_type || '') === 'organization'
  const recordName = (record.preferred_name || '').trim()
  const street = record.preferred_street || ''
  const city = record.preferred_city || ''
  const state = record.preferred_state || ''

  if (!isOrg) {
    // PERSON record — historical mapping, unchanged.
    const email = record.preferred_email || ''
    const phone = record.preferred_phone || ''
    return {
      isOrg: false,
      company: null,
      orgSubType: '',
      fields: { name: recordName, email, phone, street, city, state, zip: '', clientType: 'person' },
      matchSnapshot: { name: recordName, company: '', email, phone, street, city, state, zip: '' },
      autoFilled: { email: !!email, phone: !!phone, street: !!street, city: !!city, state: !!state },
    }
  }

  // ORGANIZATION record — org name → Organization Name field only.
  const personName = (record.primary_person_name || '').trim()
  const personEmail = (record.primary_person_email || '').trim()
  const personPhone = (record.primary_person_phone || '').trim()
  // With a primary contact person on file the card prefills from that person
  // (per-field fallback to the record-level value); without one the typed
  // name stays and email/phone come from the record-level values as before.
  const name = personName || (typedName || '').trim()
  const email = personEmail || record.preferred_email || ''
  const phone = personPhone || record.preferred_phone || ''
  return {
    isOrg: true,
    company: recordName,
    orgSubType: record.org_primary_sub_type || '',
    fields: { name, email, phone, street, city, state, zip: '', clientType: 'organization' },
    matchSnapshot: { name, company: recordName, email, phone, street, city, state, zip: '' },
    autoFilled: { email: !!email, phone: !!phone, street: !!street, city: !!city, state: !!state },
  }
}
